import {type ChangeEvent, type Dispatch, type SetStateAction} from 'react';

import {StyledAdornedEnd} from 'styles/InputMenu';

import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import GenericTextField from 'components/Generic/TextField';
import {handleKeyDown} from 'components/InputMenu/helpers';
import {useInputMenuContext} from 'context/InputMenu';
import NumberFormatCustom from 'helpers/NumberFormatter';
import type * as I from 'interfaces';
import {useTranslation} from 'react-i18next';

import {Stack} from '@mui/material';
import {type HarvestDemandPutSpeciesEnum, type HarvestDemandPutCertificatesEnum} from 'client';
import {validateCage} from './Cage';
import {CertificatesSelector} from './CertificatesSelector';
import {SpeciesSelector} from './SpeciesSelector';
import useParseURL from 'hooks/useParseURL';
import {useGetPlanFishTypes} from 'hooks/swr';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {CompanySelector} from 'components/Shared/CompanySelector';
import {useGetDefaultCompany} from 'hooks/useGetDefaultCompany';
import {FishGroupPropertiesTags} from 'components/Shared/FishGroupPropertiesTags';
import {useGetIsChilePlan} from 'hooks/useGetIsChilePlan';

const AddEditCage = ({
  cage,
  setCage,
  setCreateUpdateCage,
}: {
  cage: I.ICage;
  setCage: Dispatch<SetStateAction<I.ICage | undefined>>;
  setCreateUpdateCage: Dispatch<SetStateAction<boolean>>;
}) => {
  const {t} = useTranslation('common');
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {planId} = useParseURL();
  const defaultCompany = useGetDefaultCompany();
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});
  const {isChilePlan} = useGetIsChilePlan();
  const handleChange = (prop: keyof I.ICage) => (event: ChangeEvent<HTMLInputElement>) => {
    setCage({...cage, [prop]: event.target.value});
  };

  return (
    <Stack spacing={2}>
      {/* Cage Input Date */}
      <InputMenuDatePicker
        value={cage.inputDate}
        onChange={(date) => {
          date && setCage({...cage, inputDate: date});
        }}
      />

      {/* Cage Initial Count */}
      <GenericTextField
        inputProps={{'data-testid': 'count'}}
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          endAdornment: <StyledAdornedEnd>{t('fish')}</StyledAdornedEnd>,
        }}
        label={t('count')}
        value={cage.initialCount > 0 ? cage.initialCount : null}
        onChange={handleChange('initialCount')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
        helperText={t(validateCage(cage, 'initialCount'))}
        error={validateCage(cage, 'initialCount').length > 0}
      />

      {/* Cage Initial Weight */}
      <GenericTextField
        inputProps={{'data-testid': 'weight'}}
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          endAdornment: <StyledAdornedEnd>{t('grams')}</StyledAdornedEnd>,
        }}
        label={t('averageWeight')}
        value={cage?.initialWeight > 0 ? Math.round(cage.initialWeight * 100) / 100 : ''}
        onChange={handleChange('initialWeight')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
        helperText={t(validateCage(cage, 'initialWeight'))}
        error={validateCage(cage, 'initialWeight').length > 0}
      />

      {/* Cage Accumulated Biomass Harvested */}
      {isChilePlan && (
        <>
          <GenericTextField
            inputProps={{'data-testid': 'accumulated-biomass-harvested'}}
            InputProps={{
              inputComponent: NumberFormatCustom as (props) => JSX.Element,
              endAdornment: <StyledAdornedEnd>{t('tons')}</StyledAdornedEnd>,
            }}
            label={t('accumulatedBiomassHarvested')}
            value={
              cage?.accumulatedBiomassHarvested > 0 ? Math.round(cage.accumulatedBiomassHarvested * 100) / 100 : ''
            }
            onChange={handleChange('accumulatedBiomassHarvested')}
            onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
            helperText={t(validateCage(cage, 'accumulatedBiomassHarvested'))}
            error={validateCage(cage, 'accumulatedBiomassHarvested').length > 0}
          />

          {/* Cage Accumulated Biomass Lost */}
          <GenericTextField
            inputProps={{'data-testid': 'accumulated-biomass-lost'}}
            InputProps={{
              inputComponent: NumberFormatCustom as (props) => JSX.Element,
              endAdornment: <StyledAdornedEnd>{t('tons')}</StyledAdornedEnd>,
            }}
            label={t('accumulatedBiomassLost')}
            value={cage?.accumulatedBiomassLost > 0 ? Math.round(cage.accumulatedBiomassLost * 100) / 100 : ''}
            onChange={handleChange('accumulatedBiomassLost')}
            onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
            helperText={t(validateCage(cage, 'accumulatedBiomassLost'))}
            error={validateCage(cage, 'accumulatedBiomassLost').length > 0}
          />
        </>
      )}

      <GenericTextField
        inputProps={{'data-testid': 'fish-group-name'}}
        label={t('fishGroupName')}
        value={cage?.fishGroupName ?? ''}
        onChange={handleChange('fishGroupName')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />
      <GenericTextField
        label={t('fishGroupId')}
        inputProps={{'data-testid': 'fish-group-id'}}
        value={cage?.fishGroupInventoryId ?? ''}
        onChange={handleChange('fishGroupInventoryId')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />

      {/* Fish Type Picker */}
      <GenericSelect
        inputProps={{'data-testid': 'fish-type'}}
        value={cage?.fishTypeId}
        onChange={(event) => {
          const fishTypeId = event.target.value as string;
          setCage({...cage, fishTypeId});
        }}
        label={t('fishType')}
        data-testid="fish-type-control"
      >
        {fishTypes &&
          fishTypes.map((fishType) => (
            <GenericSelectItem key={fishType.id} value={fishType.id} data-testid={`fish-type-${fishType.name}`}>
              {fishType.name}
            </GenericSelectItem>
          ))}
      </GenericSelect>

      {/* Fish Species */}
      <SpeciesSelector
        species={cage.species}
        setSpecies={(species?: HarvestDemandPutSpeciesEnum) => {
          species && setCage({...cage, species});
        }}
      />

      {/* Fish Certificates */}
      <CertificatesSelector
        certificates={cage.certificates ?? []}
        setCertificates={(certificates: HarvestDemandPutCertificatesEnum[]) => {
          setCage({...cage, certificates});
        }}
      />

      <FishGroupPropertiesTags
        label={t('fishGroupProperties')}
        helperText={t('fishGroupPropertiesHelperText')}
        selectedTags={cage.fishGroupPropertyIds}
        onAfterCreate={(newProperty) => {
          setCage({...cage, fishGroupPropertyIds: [...cage.fishGroupPropertyIds, newProperty]});
        }}
        onSelectionChange={(selectedFishGroupProperties) => {
          setCage({...cage, fishGroupPropertyIds: selectedFishGroupProperties});
        }}
      />

      {/* Company */}
      <CompanySelector
        planId={planId}
        companyId={cage.companyId ?? defaultCompany?.id ?? ''}
        setCompanyId={(companyId) => {
          setCage({...cage, companyId});
        }}
      />
    </Stack>
  );
};

export default AddEditCage;
